/**
 * Who gets the onboarding welcome flow — the rules alone, importing nothing.
 *
 * Split out of useOnboardingGate so it can be tested. Everything in this repo's
 * vitest setup runs in a node environment, and useOnboardingGate reaches
 * useAccessControl, which transitively pulls in modules that touch `window` at
 * import time (DiagnosticEventLog registers a beforeunload listener in its
 * constructor). A rule this load-bearing shouldn't be untestable because of
 * what its neighbours import.
 *
 * Typed structurally rather than against AccessControl so this file stays free
 * of imports; AccessControl satisfies it.
 */
export type OnboardingEligibilityInput = {
  authenticated: boolean;
  isInternalUser: boolean;
  subscriptionQuery: { data?: { status?: string } | undefined };
  /**
   * The Keycloak `onboarding_enabled` user attribute, read by
   * readOnboardingEnabledClaim. How corporate seats — which have no Chargebee
   * record, so never read as in_trial — are put in the audience.
   */
  onboardingEnabled: boolean;
  /**
   * Viewport wide enough to run the flow. See shared/hooks/useGuideViewport.
   *
   * Passed in rather than measured here so this file stays import-free, for
   * the same reason the rest of it is.
   */
  viewportSupported: boolean;
};

/**
 * Two audiences, deliberately kept as separate arms: self-serve trialists
 * (Chargebee `in_trial`) and corporate seats flagged at provisioning. Either is
 * enough. Neither decides whether the flow has already run — that is
 * onboarding.state, the same for both.
 *
 * `authenticated` is checked explicitly because the logged-out guided demo keys
 * off `!authenticated` — see demo-guide/__tests__/gateExclusivity.test.ts,
 * which exists to fail loudly if the two audiences are ever made to overlap.
 * The trial arm only ever implied it (the subscription query is disabled when
 * logged out); the flag arm should not lean on the same accident.
 *
 * The viewport gate is shared with that demo for the same reason: the rail and
 * the checklist launcher are both `hidden sm:block`, so on a phone the welcome
 * flow ran and then handed over to nothing.
 */
export function isOnboardingEligible(
  access: OnboardingEligibilityInput
): boolean {
  if (!access.authenticated) return false;
  if (access.isInternalUser) return false;
  if (!access.viewportSupported) return false;
  return (
    access.subscriptionQuery.data?.status === "in_trial" ||
    access.onboardingEnabled
  );
}

/**
 * The `onboarding_enabled` claim, as mapped from the Keycloak user attribute.
 *
 * The attribute is a select of "true" / "false", and blank on every account
 * provisioned before it existed — blank means not in the audience. Boolean
 * `true` is accepted too, in case the mapper's JSON type is ever switched from
 * String; anything else is no.
 */
export function readOnboardingEnabledClaim(
  tokenParsed: Record<string, unknown> | undefined
): boolean {
  const claim = tokenParsed?.onboarding_enabled;
  return claim === "true" || claim === true;
}

export type OnboardingOverrideAction = "force" | "reset" | "off";

/**
 * What a page URL asks of the onboarding override.
 *
 *   ?setup=start       — the customer-facing link, honoured in EVERY build:
 *                        re-runs the welcome flow for whoever clicks it.
 *   ?onboarding=force  — dev/preview only: treat this account as an eligible
 *                        fresh trialist.
 *   ?onboarding=reset  — dev/preview only: wipe saved state, then re-run.
 *   ?onboarding=off    — dev/preview only: stop overriding.
 *
 * `fromLink` is what lets production tell the two apart once they are sitting
 * in sessionStorage — see override.ts.
 */
export function readSetupParam(
  search: string,
  isProduction: boolean
): { action: OnboardingOverrideAction; fromLink: boolean } | null {
  const params = new URLSearchParams(search);
  if (params.get("setup") === "start") {
    return { action: "reset", fromLink: true };
  }
  if (isProduction) return null;
  const value = params.get("onboarding");
  if (value === "force" || value === "reset" || value === "off") {
    return { action: value, fromLink: false };
  }
  return null;
}
